import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Lock, AlertCircle } from 'lucide-react';
import { CheckoutShell } from './CheckoutShell.jsx';
import { StepFrame, StepButton } from './CheckoutSteps.jsx';
import { CheckoutNotFound } from './CheckoutNotFound.jsx';
import { CheckoutOutcome } from './CheckoutOutcome.jsx';
import { EmbeddedCheckout } from './EmbeddedCheckout.jsx';
import { MethodPicker } from './MethodPicker.jsx';
import { OfflineInstructions, OfflineRecap } from './OfflineInstructions.jsx';
import { CardPreview } from '../../checkout/CardPreview.jsx';
import { CardBrandIcon } from '../../checkout/CardBrandIcon.jsx';
import { useResource } from '../../hooks/useApi.js';
import { http } from '../../lib/api.js';
import { formatMoney } from '../../lib/format.js';
import { getTokenizer } from '../../checkout/tokenizers/index.js';
import { postOutcome, isEmbedded } from '../../checkout/embedBridge.js';
import { playOutcome } from '../../lib/sound.js';
import {
  formatCardNumber, formatExpiry, parseExpiry, detectBrand, digitsOnly,
  validateCardNumber, validateExpiry, validateCvc, validatePhone, formatPhone,
} from '../../checkout/cardUtils.js';

const EMPTY_CARD = { number: '', expiry: '', cvc: '', holder: '' };
const CARD = 'card';

/**
 * HC-6: "Card inputs carry no `name` attribute and are never part of a form
 * submission. They are read in JavaScript and cleared after tokenization."
 *
 * The missing `name` is the real protection: if JavaScript fails and the browser
 * performs a native submit, an input without a name is not serialised at all, so
 * no card digit can reach our server.
 */
function Field({ id, label, value, onChange, onFocus, onBlur, error, trailing, hint, ...props }) {
  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <label htmlFor={id} className="text-[13px] font-medium text-ink-soft">{label}</label>
        <AnimatePresence mode="wait">
          {error && (
            <motion.span
              key={error}
              initial={{ opacity: 0, y: -3 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.16 }}
              className="flex items-center gap-1 text-[11px] font-medium text-red-600"
            >
              <AlertCircle className="h-3 w-3" aria-hidden="true" />
              {error}
            </motion.span>
          )}
        </AnimatePresence>
      </div>

      <div className="relative">
        <input
          id={id}
          /* no name attribute — see above */
          value={value}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          aria-invalid={error ? 'true' : undefined}
          className={`w-full rounded-lg border bg-surface px-3.5 py-3 text-[15px] text-ink outline-none transition-all duration-150 placeholder:text-faint ${
            error
              ? 'border-red-400 shadow-[0_0_0_3px_rgb(var(--red-500)/0.12)]'
              : 'border-line hover:border-slate-300 focus:border-[color:var(--accent-brand)] focus:shadow-[0_0_0_3px_rgb(var(--accent)/0.16)]'
          } ${trailing ? 'pr-14' : ''}`}
          {...props}
        />
        {trailing && <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">{trailing}</span>}
      </div>

      {hint && !error && <p className="mt-1.5 text-xs text-muted">{hint}</p>}
    </div>
  );
}

export function CheckoutPage() {
  const { sessionId } = useParams();
  const { data: session, error: loadError, loading } = useResource(`/checkout/sessions/${sessionId}`);

  const [card, setCard] = useState(EMPTY_CARD);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  // Kept because the merchant may have supplied one; the card step no longer
  // asks, since a postcode AVS check is not something this platform performs.
  const [postalCode] = useState('');
  const [errors, setErrors] = useState({});
  const [focused, setFocused] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const [formError, setFormError] = useState(null);
  const [shake, setShake] = useState(0);
  const [method, setMethod] = useState(null);
  const [offline, setOffline] = useState(null);

  /**
   * One decision per screen.
   *
   * `contact` collects who to reach, `method` asks how they want to pay,
   * `detail` is that method's own form. Everything used to be stacked in one
   * scrolling column, so a customer had to explore the page to find the next
   * thing to do — and a checkout that has to be explored is one people abandon.
   */
  const [step, setStep] = useState('contact');

  // HC-12, browser half: the server's atomic claim is the real guarantee, but
  // there is no reason to let a double-click become two requests either.
  const inFlight = useRef(false);
  const brand = detectBrand(card.number);

  const checkOnBlur = (field) => {
    setFocused(null);
    if (!card[field]) return;
    const check = {
      number: () => validateCardNumber(card.number),
      expiry: () => validateExpiry(card.expiry),
      cvc: () => validateCvc(card.cvc, card.number),
    }[field];
    if (check) setErrors((e) => ({ ...e, [field]: check() }));
  };

  /**
   * Every way this customer may pay, in one list.
   *
   * HC-2: the card row is labelled by what the customer does, never by whoever
   * processes it. A customer choosing here is choosing how to pay the merchant;
   * which gateway sits behind the card row is not information they need.
   */
  const methods = useMemo(() => {
    if (!session) return [];

    const card = session.flow
      ? [{
          key: CARD,
          icon: 'card',
          label: session.flow === 'direct' ? 'Card' : 'Pay online',
          blurb: session.flow === 'direct'
            ? 'Visa, Mastercard, American Express and more.'
            : 'Card, UPI, net banking or a wallet.',
        }]
      : [];

    return [...card, ...(session.offlineMethods ?? [])];
  }, [session]);

  // The first method is selected for them. A checkout that opens on "choose one"
  // is a checkout with an extra click in front of the common case.
  useEffect(() => {
    if (methods.length > 0 && method === null) setMethod(methods[0].key);
  }, [methods, method]);

  /**
   * Whatever the merchant already knows about this customer, filled in for them.
   *
   * Only once, and only into an empty field: re-running this on every session
   * refresh would overwrite what the customer is in the middle of typing.
   */
  const prefilled = useRef(false);
  useEffect(() => {
    if (!session?.customer || prefilled.current) return;
    prefilled.current = true;
    if (session.customer.phone) setPhone(session.customer.phone);
    if (session.customer.email) setEmail(session.customer.email);
  }, [session]);

  const finish = useCallback((result) => {
    setOutcome(result);
    // Audible, because the person who most needs to know is often not looking
    // at the screen — a cashier bagging goods, a customer putting a card away.
    playOutcome(result.result);
    // Tells a merchant's page, when this checkout is running inside their site
    // as a drop-in, that it is over. A no-op when we are the top-level page.
    postOutcome(result);

    /**
     * Running as a drop-in, the return URL belongs to the page *around* us. If
     * this frame navigated there, the merchant's thank-you page would render
     * inside a payment modal. Their `onSuccess` has already fired; where to go
     * next is theirs to decide.
     */
    if (result.result === 'approved' && result.returnUrl && !isEmbedded()) {
      setTimeout(() => { window.location.href = result.returnUrl; }, 2200);
    }
  }, []);

  const pay = useCallback(async () => {
    if (inFlight.current || !session) return;

    /**
     * HC-8: validate before spending a provider call. Each of these is a mistake
     * the customer can fix themselves; sending it to the network returns a
     * decline, which reads as "your card is broken" when it was a typo.
     */
    const next = {
      phone: validatePhone(phone),
      number: validateCardNumber(card.number),
      expiry: validateExpiry(card.expiry),
      cvc: validateCvc(card.cvc, card.number),
    };
    if (Object.values(next).some(Boolean)) {
      setErrors(next);
      setFormError(null);
      setShake((n) => n + 1);
      return;
    }

    setErrors({});
    setFormError(null);
    inFlight.current = true;
    setSubmitting(true);

    try {
      const tokenizer = getTokenizer(session.tokenization.library);

      // HC-4: the card goes from this browser straight to the payment network.
      // Our servers are not on this path at all.
      const token = await tokenizer.tokenize({
        config: session.tokenization,
        card: { number: digitsOnly(card.number), expiry: parseExpiry(card.expiry), cvc: card.cvc },
        billing: { postalCode, name: card.holder },
      });

      // HC-6: cleared the moment a token exists.
      setCard(EMPTY_CARD);

      // HC-5: a token, an email and non-card billing fields only. The server
      // rejects — not strips — anything card-shaped, so this body is minimal.
      const result = await http.post(`/checkout/sessions/${sessionId}/confirm`, {
        token,
        phone,
        ...(email ? { email } : {}),
        ...(postalCode ? { billing: { postalCode } } : {}),
      });

      if (result.result === 'declined') {
        // HC-10: the reason, and the form ready for another card.
        setFormError(result.message);
        setShake((n) => n + 1);
        playOutcome('declined');
        if (!result.canRetry) finish(result);
        return;
      }

      finish(result);
    } catch (err) {
      setCard(EMPTY_CARD);
      setFormError(err?.message ?? 'We could not process that payment. Please try again.');
      setShake((n) => n + 1);
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }, [card, email, phone, postalCode, session, sessionId, finish]);

  /**
   * An embedded widget has handed back its result; the server decides what it
   * means. Shares every outcome path with the direct flow above — there is one
   * set of HC-10 outcomes, not one per provider.
   */
  const confirmEmbedded = useCallback(async (widgetResult) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);
    setFormError(null);

    try {
      const result = await http.post(`/checkout/sessions/${sessionId}/confirm-embedded`, {
        result: widgetResult,
        ...(phone ? { phone } : {}),
        ...(email ? { email } : {}),
      });

      if (result.result === 'declined') {
        setFormError(result.message);
        setShake((n) => n + 1);
        if (!result.canRetry) finish(result);
        return;
      }

      finish(result);
    } catch (err) {
      setFormError(err?.message ?? 'We could not confirm that payment. Please try again.');
      setShake((n) => n + 1);
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }, [email, phone, sessionId, finish]);

  /**
   * The customer says they will pay offline.
   *
   * This never produces `approved`. No processor saw any money move, so the
   * payment goes to awaiting-confirmation and stays there until a human at the
   * merchant confirms it arrived.
   */
  const chooseOffline = useCallback(async (chosen) => {
    if (inFlight.current) return;

    /**
     * An offline payment needs this more than a card does. There is no gateway
     * to ask what happened, so the customer's phone number is the merchant's
     * entire recovery path when a promised transfer never arrives.
     */
    const phoneError = validatePhone(phone);
    if (phoneError) {
      setErrors((e) => ({ ...e, phone: phoneError }));
      setShake((n) => n + 1);
      return;
    }

    inFlight.current = true;
    setSubmitting(true);
    setFormError(null);

    try {
      const result = await http.post(`/checkout/sessions/${sessionId}/offline`, {
        method: chosen.key,
        phone,
        ...(email ? { email } : {}),
      });
      setOffline(result);
      // Deliberately the failure tone: nothing has arrived yet, and a success
      // chime here would tell a cashier to hand over the goods.
      playOutcome('awaiting_confirmation');
      postOutcome({ result: 'awaiting_confirmation', payment: result.payment });
    } catch (err) {
      setFormError(err?.message ?? 'We could not record that. Please try again.');
      setShake((n) => n + 1);
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }, [sessionId, email, phone]);

  /* ------------------------------ render ------------------------------ */

  if (loading) {
    return (
      <CheckoutShell merchant={null} payment={null} showTrust={false}>
        <div className="grid place-items-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-faint" role="status" aria-label="Loading" />
        </div>
      </CheckoutShell>
    );
  }

  if (loadError) return <CheckoutNotFound message={loadError.status === 404 ? undefined : loadError.message} />;

  if (session.status === 'expired' || session.status === 'cancelled') {
    return (
      <CheckoutOutcome merchant={session.merchant} payment={session.payment}
        result={session.status === 'expired' ? 'expired' : 'cancelled'} />
    );
  }
  if (session.status === 'completed' && !outcome && !offline) {
    return <CheckoutOutcome merchant={session.merchant} result="already_paid" payment={session.payment} />;
  }
  /**
   * The provider has not decided yet (UPI, wallets) or a confirmation is mid
   * flight. Rendering the form here invites a second payment, which is the
   * one thing this screen must never do.
   */
  if (session.status === 'processing' && !outcome && !offline) {
    return <CheckoutOutcome merchant={session.merchant} result="in_progress" payment={session.payment} />;
  }

  if (offline) {
    return (
      <CheckoutOutcome
        merchant={session.merchant}
        result="awaiting_confirmation"
        payment={offline.payment ?? session.payment}
        extra={<OfflineRecap method={offline.method} reference={offline.reference} />}
      />
    );
  }

  if (outcome) {
    return (
      <CheckoutOutcome
        merchant={session.merchant} result={outcome.result} message={outcome.message}
        payment={outcome.payment ?? session.payment} returnUrl={outcome.returnUrl}
      />
    );
  }

  const accent = session.merchant.accentColor;
  const selected = methods.find((m) => m.key === method) ?? methods[0] ?? null;

  /**
   * No provider and no offline method means the merchant cannot be paid at all.
   * Saying so is better than an empty panel the customer stares at.
   */
  if (!selected) {
    return <CheckoutOutcome merchant={session.merchant} result="cancelled" payment={session.payment}
      message="This merchant has not finished setting up payments. Nothing has been charged." />;
  }

  /* ------------------------------- the steps ------------------------------ */

  /**
   * Which screens this customer actually sees.
   *
   * Both of the first two are skipped when there is nothing to decide: a phone
   * the merchant already supplied, or a single way to pay. A step that asks a
   * question with one possible answer is a step that should not exist.
   */
  const needsContact = !session.customer?.phone;
  const needsMethodChoice = methods.length > 1;
  const stepOrder = [
    ...(needsContact ? ['contact'] : []),
    ...(needsMethodChoice ? ['method'] : []),
    'detail',
  ];

  // Land on the first step that has a question on it.
  const current = stepOrder.includes(step) ? step : stepOrder[0];

  const goBack = () => {
    const i = stepOrder.indexOf(current);
    if (i > 0) { setStep(stepOrder[i - 1]); setFormError(null); }
  };

  const errorBanner = formError && (
    <div role="alert" className="mb-4 flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-3.5 py-3">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
      {/* HC-9: an instruction, never a raw code. HC-14: never blames them. */}
      <p className="text-sm font-medium leading-relaxed text-red-700">{formError}</p>
    </div>
  );

  const frame = (props) => (
    <CheckoutShell merchant={session.merchant} payment={session.payment} bare>
      <StepFrame
        merchant={session.merchant}
        payment={session.payment}
        step={current}
        steps={stepOrder}
        onBack={stepOrder.indexOf(current) > 0 ? goBack : undefined}
        {...props}
      />
    </CheckoutShell>
  );

  /* ------------------------------- 1. contact ----------------------------- */

  if (current === 'contact') {
    const advance = () => {
      const problem = validatePhone(phone);
      if (problem) { setErrors((e) => ({ ...e, phone: problem })); return; }
      setErrors((e) => ({ ...e, phone: null }));
      setStep(stepOrder[1]);
    };

    return frame({
      title: 'Where can we reach you?',
      blurb: 'So the merchant can confirm this payment with you.',
      children: (
        <form onSubmit={(e) => { e.preventDefault(); advance(); }} noValidate>
          <div className="space-y-4">
            <Field
              id="cc-phone" label="Phone" type="tel" inputMode="tel" autoComplete="tel"
              placeholder="+91 98765 43210" value={phone} error={errors.phone}
              autoFocus
              onChange={(e) => {
                setPhone(formatPhone(e.target.value));
                if (errors.phone) setErrors((x) => ({ ...x, phone: null }));
              }}
              onBlur={() => { if (phone) setErrors((e) => ({ ...e, phone: validatePhone(phone) })); }}
            />

            <Field
              id="cc-email" label="Email" type="email" inputMode="email" autoComplete="email"
              placeholder="Optional" value={email}
              hint="For your receipt."
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <StepButton accent={accent} type="submit">Continue</StepButton>
        </form>
      ),
    });
  }

  /* -------------------------------- 2. method ----------------------------- */

  if (current === 'method') {
    return frame({
      title: 'How would you like to pay?',
      children: (
        <>
          {errorBanner}
          <MethodPicker
            methods={methods}
            selected={selected.key}
            accent={accent}
            bare
            onSelect={(key) => { setMethod(key); setFormError(null); setStep('detail'); }}
          />
        </>
      ),
    });
  }

  /* -------------------------------- 3. detail ----------------------------- */

  if (selected.key !== CARD) {
    return frame({
      children: (
        <>
          {errorBanner}
          <OfflineInstructions
            method={selected}
            payment={session.payment}
            accent={accent}
            reference={session.payment.orderId ?? session.payment.id}
            submitting={submitting}
            onConfirm={() => chooseOffline(selected)}
          />
        </>
      ),
    });
  }

  /**
   * The server decides how this provider takes a card; the page just renders it.
   * Nothing below names a provider, so adding one changes the registries and not
   * this file (G6).
   */
  if (session.flow === 'embedded' || session.flow === 'hosted_redirect') {
    return frame({
      title: 'Complete your payment',
      blurb: 'Pay securely without leaving this page.',
      children: (
        <>
          {errorBanner}
          <EmbeddedCheckout
            session={session}
            merchant={session.merchant}
            payment={session.payment}
            onResult={confirmEmbedded}
            onError={(err) => setFormError(err?.message ?? null)}
          />
        </>
      ),
    });
  }

  return frame({
    title: 'Card details',
    children: (
      <motion.form
        key={shake}
        animate={shake ? { x: [0, -9, 8, -5, 3, 0] } : {}}
        transition={{ duration: 0.42, ease: 'easeOut' }}
        className="space-y-4"
        onSubmit={(e) => { e.preventDefault(); pay(); }}
        noValidate
      >
        {errorBanner}

        <div className="mb-5">
          <CardPreview
            number={card.number}
            expiry={card.expiry}
            cvc={card.cvc}
            holder={card.holder}
            flipped={focused === 'cvc'}
            focused={focused}
          />
        </div>

        <Field
          id="cc-number" label="Card number" type="text" inputMode="numeric"
          autoComplete="cc-number" placeholder="1234 1234 1234 1234"
          value={card.number} error={errors.number}
          trailing={<CardBrandIcon brand={brand.brand} />}
          autoFocus
          onFocus={() => setFocused('number')}
          onBlur={() => checkOnBlur('number')}
          onChange={(e) => {
            setCard((c) => ({ ...c, number: formatCardNumber(e.target.value) }));
            if (errors.number) setErrors((x) => ({ ...x, number: null }));
          }}
        />

        <Field
          id="cc-holder" label="Name on card" type="text" autoComplete="cc-name"
          placeholder="A. Customer" value={card.holder}
          onFocus={() => setFocused('holder')}
          onBlur={() => setFocused(null)}
          onChange={(e) => setCard((c) => ({ ...c, holder: e.target.value }))}
        />

        <div className="grid grid-cols-2 gap-3">
          <Field
            id="cc-expiry" label="Expiry" type="text" inputMode="numeric"
            autoComplete="cc-exp" placeholder="MM / YY"
            value={card.expiry} error={errors.expiry}
            onFocus={() => setFocused('expiry')}
            onBlur={() => checkOnBlur('expiry')}
            onChange={(e) => {
              setCard((c) => ({ ...c, expiry: formatExpiry(e.target.value) }));
              if (errors.expiry) setErrors((x) => ({ ...x, expiry: null }));
            }}
          />
          <Field
            id="cc-cvc" label="Security code" type="text" inputMode="numeric"
            autoComplete="cc-csc" maxLength={4}
            placeholder={brand.cvcLength === 4 ? '1234' : '123'}
            value={card.cvc} error={errors.cvc}
            onFocus={() => setFocused('cvc')}
            onBlur={() => checkOnBlur('cvc')}
            onChange={(e) => {
              setCard((c) => ({ ...c, cvc: digitsOnly(e.target.value).slice(0, 4) }));
              if (errors.cvc) setErrors((x) => ({ ...x, cvc: null }));
            }}
          />
        </div>

        {/*
          * Only the simulator sends these, so a real provider's checkout can
          * never display them. Without this panel the merchant has no way to
          * reach a decline, and a payment product nobody can test declining is
          * a payment product nobody has really tested.
          */}
        {session.tokenization?.testCards?.length > 0 && (
          <details className="rounded-lg border border-dashed" style={{ borderColor: 'rgb(var(--line))' }}>
            <summary className="cursor-pointer px-3.5 py-2.5 text-xs font-medium text-muted">
              Test cards
            </summary>
            <div className="space-y-1 px-3.5 pb-3">
              {session.tokenization.testCards.map((tc) => (
                <button
                  key={tc.number}
                  type="button"
                  onClick={() => setCard((c) => ({ ...c, number: tc.number, expiry: '12 / 34', cvc: '123' }))}
                  className="flex w-full items-center justify-between gap-3 rounded px-1.5 py-1 text-left transition-colors hover:bg-surface-2"
                >
                  <span className="font-mono text-[12px] text-ink-soft">{tc.number}</span>
                  <span className="text-[11px] text-muted">{tc.label}</span>
                </button>
              ))}
              <p className="pt-1 text-[11px] text-faint">Any other number approves.</p>
            </div>
          </details>
        )}

        {/* HC-1: the pay button uses the merchant's accent colour. */}
        <StepButton accent={accent} busy={submitting} type="submit">
          <AnimatePresence mode="wait" initial={false}>
            {submitting ? (
              <motion.span
                key="busy"
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18 }}
                className="flex items-center gap-2"
              >
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Processing…
              </motion.span>
            ) : (
              <motion.span
                key="idle"
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18 }}
                className="flex items-center gap-2"
              >
                <Lock className="h-3.5 w-3.5" aria-hidden="true" />
                Pay {formatMoney(session.payment.amount, session.payment.currency)}
              </motion.span>
            )}
          </AnimatePresence>
        </StepButton>

        {session.attemptsRemaining <= 2 && session.attemptsRemaining > 0 && (
          <p className="text-center text-xs text-muted">
            {session.attemptsRemaining} attempt{session.attemptsRemaining === 1 ? '' : 's'} remaining on this link.
          </p>
        )}
      </motion.form>
    ),
  });
}

export default CheckoutPage;
